import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Mail, ArrowRight, Loader2, Lock, User, Shield } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

// Use proxy in development to avoid CORS issues
// In development: /api/v1 (proxied to https://api.blonix.io/api/v1)
// In production: https://api.blonix.io/api/v1
const API_BASE_URL = process.env.NODE_ENV === 'production'
  ? 'https://api.blonix.io/api/v1'
  : '/api/v1';

export const SignupForm = () => {
  const { t } = useLanguage();
  const { referralId } = useParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [otp, setOtp] = useState('');
  const [step, setStep] = useState('register'); // 'register' or 'verify'
  const [isLoading, setIsLoading] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [shakeCheckbox, setShakeCheckbox] = useState(false);

  // Shake animation variant
  const shakeAnimation = {
    x: [0, -10, 10, -10, 10, -5, 5, 0],
    transition: {
      duration: 0.5,
      ease: 'easeInOut',
    },
  };

  // Validate form fields
  const validateForm = () => {
    if (!email) {
      toast.error(t('signup.errors.email'));
      return false;
    }
    if (!password) {
      toast.error(t('signup.errors.password'));
      return false;
    }
    if (password.length < 8) {
      toast.error(t('signup.errors.passwordLength'));
      return false;
    }
    if (password !== repeatPassword) {
      toast.error(t('signup.errors.passwordMatch'));
      return false;
    }
    if (!agreedToTerms) {
      toast.error(t('signup.errors.terms'));
      // Trigger shake animation
      setShakeCheckbox(true);
      setTimeout(() => setShakeCheckbox(false), 500);
      return false;
    }
    return true;
  };

  // Step 1: Send OTP
  const handleSendOTP = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);

    try {
      const response = await axios.post(
        `${API_BASE_URL}/user/send-register-otp`,
        { email },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setIsLoading(false);

      if (response.status === 200 || response.status === 201) {
        toast.success(t('signup.errors.otpSentSuccess'));
        setStep('verify');
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to send OTP. Please try again.';
      toast.error(errorMessage);
    }
  };

  // Step 2: Register with OTP
  const handleRegister = async (e) => {
    e.preventDefault();

    if (!otp || otp.length !== 6) {
      toast.error(t('signup.errors.otpInvalid'));
      return;
    }

    setIsLoading(true);

    try {
      const registerData = {
        email,
        password,
        otp,
      };

      // Add referralCode only if it exists
      if (referralId) {
        registerData.referralCode = referralId;
      }

      const response = await axios.post(
        `${API_BASE_URL}/user/auth/register`,
        registerData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      setIsLoading(false);

      if (response.status === 200 || response.status === 201) {
        toast.success(t('signup.errors.registerSuccess'));

        // Reset form
        setEmail('');
        setPassword('');
        setRepeatPassword('');
        setOtp('');
        setStep('register');
        setAgreedToTerms(false);
      }
    } catch (error) {
      setIsLoading(false);
      const errorMessage = error.response?.data?.message || error.message || 'Registration failed. Please try again.';
      toast.error(errorMessage);

      // If OTP is invalid, allow user to go back or resend
      if (error.response?.status === 400 || error.response?.status === 401) {
        // Optionally reset OTP field
        setOtp('');
      }
    }
  };

  const handleBackToRegister = () => {
    setStep('register');
    setOtp('');
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.4 }}
      className="w-full space-y-4"
    >
      <AnimatePresence mode="wait">
        {step === 'register' ? (
          <motion.form
            key="register"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 20 }}
            transition={{ duration: 0.3 }}
            onSubmit={handleSendOTP}
            className="space-y-3"
          >
            <div className="relative">
              <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="email"
                placeholder={t('signup.emailPlaceholder')}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-12 h-14 text-base bg-card/80 border-border/50 focus:border-primary"
                disabled={isLoading}
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="password"
                placeholder={t('signup.passwordPlaceholder')}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="pl-12 h-14 text-base bg-card/80 border-border/50 focus:border-primary"
                disabled={isLoading}
              />
            </div>

            <div className="relative">
              <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="password"
                placeholder={t('signup.repeatPasswordPlaceholder')}
                value={repeatPassword}
                onChange={(e) => setRepeatPassword(e.target.value)}
                className="pl-12 h-14 text-base bg-card/80 border-border/50 focus:border-primary"
                disabled={isLoading}
              />
            </div>

            {/* Referral Code Field */}
            <div className="relative">
              <User className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('signup.referralPlaceholder')}
                value={referralId || ''}
                readOnly
                className="pl-12 h-14 text-base bg-card/50 border-border/50 cursor-not-allowed opacity-70"
              />
            </div>

            {/* Terms with Checkbox */}
            <motion.div
              className="flex items-start gap-2 pb-2"
              animate={shakeCheckbox ? shakeAnimation : {}}
              onAnimationComplete={() => setShakeCheckbox(false)}
            >
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => {
                  setAgreedToTerms(checked);
                  setShakeCheckbox(false); // Stop shaking when checked
                }}
                className="mt-0.5"
                disabled={isLoading}
              />
              <label
                htmlFor="terms"
                className="text-xs text-muted-foreground leading-relaxed cursor-pointer"
              >
                {t('signup.termsText')}{' '}
                <a href="#" className="text-primary hover:underline">{t('signup.termsLink')}</a>
                {' '}{t('signup.and')}{' '}
                <a href="#" className="text-primary hover:underline">{t('signup.privacyLink')}</a>
              </label>
            </motion.div>

            <Button
              type="submit"
              variant="cta"
              size="xl"
              className="w-full group"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  <span>{t('signup.sendingOTP')}</span>
                </>
              ) : (
                <>
                  <span>{t('signup.registerButton')}</span>
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </>
              )}
            </Button>
          </motion.form>
        ) : (
          <motion.form
            key="verify"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            transition={{ duration: 0.3 }}
            onSubmit={handleRegister}
            className="space-y-3"
          >
            <div className="text-center mb-4">
              <p className="text-sm text-muted-foreground">
                {t('signup.otpSent')} <span className="text-foreground font-semibold">{email}</span>
              </p>
            </div>

            <div className="relative">
              <Shield className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <Input
                type="text"
                placeholder={t('signup.otpPlaceholder')}
                value={otp}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                  setOtp(value);
                }}
                className="pl-12 h-14 text-base bg-card/80 border-border/50 focus:border-primary text-center text-2xl tracking-widest"
                disabled={isLoading}
                maxLength={6}
                autoFocus
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="button"
                variant="outline"
                size="xl"
                className="flex-1"
                onClick={handleBackToRegister}
                disabled={isLoading}
              >
                {t('signup.backButton')}
              </Button>

              <Button
                type="submit"
                variant="cta"
                size="xl"
                className="flex-1 group"
                disabled={isLoading || otp.length !== 6}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    <span>{t('signup.registering')}</span>
                  </>
                ) : (
                  <>
                    <span>{t('signup.verifyButton')}</span>
                    <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </Button>
            </div>

            <div className="text-center">
              <button
                type="button"
                onClick={handleSendOTP}
                disabled={isLoading}
                className="text-xs text-primary hover:underline disabled:opacity-50"
              >
                {t('signup.resendOTP')}
              </button>
            </div>
          </motion.form>
        )}
      </AnimatePresence>
    </motion.div>
  );
};
